/**
 * Registry Management Page
 * Owner view for managing gift registries
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gift, LayoutGrid, Star, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { PageState } from '@/components/layout/PageState';
import { HeroQuickMenuCard } from '@/components/shared/HeroQuickMenuCard';
import { celebrationRegistryService } from '@/lib/api/celebrationRegistry.service';
import { RegistryPlatformCard } from '@/components/registry/RegistryPlatformCard';
import { RegistryList } from '@/components/registry/RegistryList';
import { AddRegistryForm } from '@/components/registry/AddRegistryForm';
import type {
  CelebrationRegistry,
  CreateRegistryRequest,
  RegistryPlatformInfo
} from '@/types/celebrationRegistry.types';

export const RegistryManagementPage = () => {
  const { id: celebrationId } = useParams<{ id: string }>();
  const numCelebrationId = parseInt(celebrationId || '0', 10);
  const { t } = useTranslation(['registry', 'common']);

  const [registries, setRegistries] = useState<CelebrationRegistry[]>([]);
  const [platforms, setPlatforms] = useState<RegistryPlatformInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<RegistryPlatformInfo | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const primaryRegistryCount = registries.filter((registry) => registry.is_primary).length;
  const totalClicks = registries.reduce((sum, registry) => sum + registry.click_count, 0);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [numCelebrationId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [registriesData, platformsData] = await Promise.all([
        celebrationRegistryService.getRegistries(numCelebrationId),
        celebrationRegistryService.getPlatforms()
      ]);

      setRegistries(registriesData);
      setPlatforms(platformsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('registry:page.errors.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRegistryClick = (url: string) => {
    window.open(url, '_blank');
  };

  const handleAddUrlClick = (platform: RegistryPlatformInfo) => {
    setSelectedPlatform(platform);
    setAddFormOpen(true);
  };

  const handleAddRegistry = async (data: CreateRegistryRequest) => {
    try {
      setIsSubmitting(true);
      const newRegistry = await celebrationRegistryService.createRegistry(
        numCelebrationId,
        data
      );
      setRegistries([...registries, newRegistry]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditRegistry = (_registry: CelebrationRegistry) => {
    setSelectedPlatform(undefined);
    setAddFormOpen(true);
  };

  const handleDeleteRegistry = async (registryId: number) => {
    try {
      await celebrationRegistryService.deleteRegistry(numCelebrationId, registryId);
      setRegistries(registries.filter(r => r.id !== registryId));
    } catch (err) {
      throw err;
    }
  };

  if (loading) {
    return (
      <PageState
        kind="loading"
        width="wide"
        title={t('registry:page.loading.title')}
        description={t('registry:page.loading.description')}
      />
    );
  }

  return (
    <PageContainer width="wide" className="space-y-8">
        <AppBreadcrumbs
          items={[
            { label: 'Home', to: '/home' },
            { label: 'Celebrations', to: '/celebrations' },
            { label: 'Details', to: `/celebrations/${numCelebrationId}` },
            { label: 'Gift Registries' },
          ]}
          className="mb-4"
        />

        <HeroQuickMenuCard
          borderClassName="border-primary/20"
          heroClassName="bg-gradient-to-br from-primary/10 via-primary/[0.04] to-white"
          heroContent={
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center flex-shrink-0">
                  <Gift className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-xl font-bold tracking-tight text-foreground">{t('registry:page.title')}</h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('registry:page.subtitle')}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" asChild>
                  <Link to={`/celebrations/${numCelebrationId}`}>{t('registry:page.backToCelebration')}</Link>
                </Button>
              </div>
            </div>
          }
          columns={4}
          items={[
            {
              key: 'platforms',
              icon: LayoutGrid,
              iconClassName: 'text-blue-600',
              hoverClassName: 'hover:bg-blue-50',
              label: t('registry:page.stats.platforms'),
              value: platforms.length,
            },
            {
              key: 'registries',
              icon: Gift,
              iconClassName: 'text-violet-600',
              hoverClassName: 'hover:bg-violet-50',
              label: t('registry:page.stats.registries'),
              value: registries.length,
            },
            {
              key: 'primary',
              icon: Star,
              iconClassName: 'text-amber-600',
              hoverClassName: 'hover:bg-amber-50',
              label: t('registry:page.stats.primary'),
              value: primaryRegistryCount,
            },
            {
              key: 'clicks',
              icon: ExternalLink,
              iconClassName: 'text-emerald-600',
              hoverClassName: 'hover:bg-emerald-50',
              label: t('registry:page.stats.linkClicks'),
              value: totalClicks,
            },
          ]}
        />

        {/* Error Alert */}
        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Popular Platforms Section */}
        <div className="mb-12">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('registry:page.sections.popularPlatforms')}</h2>
            <p className="text-gray-600">
              {t('registry:page.sections.popularPlatformsHint')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platforms.map((platform) => (
              <RegistryPlatformCard
                key={platform.platform}
                platform={platform}
                onCreateClick={handleCreateRegistryClick}
                onAddUrlClick={handleAddUrlClick}
              />
            ))}
          </div>
        </div>

        {/* Your Registries Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('registry:page.sections.yourRegistries')} {registries.length > 0 && `(${registries.length})`}
            </h2>
            <p className="text-gray-600">
              {t('registry:page.sections.yourRegistriesHint')}
            </p>
          </div>

          <div className="p-6">
            <RegistryList
              registries={registries}
              onEdit={handleEditRegistry}
              onDelete={handleDeleteRegistry}
              isLoading={isSubmitting}
            />
          </div>
        </div>

        {/* Add Registry Form */}
        <AddRegistryForm
          open={addFormOpen}
          onOpenChange={setAddFormOpen}
          onSubmit={handleAddRegistry}
          preselectedPlatform={selectedPlatform}
          platforms={platforms}
          isLoading={isSubmitting}
        />
    </PageContainer>
  );
};
